import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";

export function TermsAndConditions({
  agreedToTerms,
  setAgreedToTerms,
  formRef,
  isOpen,
  setIsOpen,
}: {
  agreedToTerms: boolean;
  setAgreedToTerms: (agreed: boolean) => void;
  formRef?: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const accordionRef = useRef<HTMLDivElement>(null);

  // Force accordion to close when isOpen becomes false
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        const accordionTrigger = accordionRef.current?.querySelector(
          '[data-state="open"]',
        );
        if (accordionTrigger) {
          (accordionTrigger as HTMLElement).click();
        }
      }, 0);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    // Scroll to form top after a brief delay to allow accordion to close
    setTimeout(() => {
      if (formRef?.current) {
        formRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        // Fallback to accordion if no formRef provided
        accordionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  return (
    <div className="mb-2" ref={accordionRef}>
      <Accordion
        type="single"
        collapsible
        className="w-full rounded-md"
        value={isOpen ? "terms" : ""}
        onValueChange={(value) => setIsOpen(value === "terms")}
      >
        <AccordionItem value="terms" className="border-none">
          <AccordionTrigger className="py-2 text-sm font-medium items-center">
            TERMS AND CONDITIONS
          </AccordionTrigger>
          <AccordionContent className="text-sm text-gray-600 space-y-3">
            <p>
              • PayPal payments are made as "goods and services" and are covered
              under PayPal's protection policies. Fees are generally 2.9% for US
              accounts and 4.5% for accounts outside the US. PayPal might hold
              funds for 21 days or longer based on your seller history.{" "}
            </p>
            <p>
              • If choosing Echeck, Zelle, or Wire, payment requires approval by
              our verification team. Not all accounts are eligible. Payments may
              take longer than 6 weeks, with possible partial payments. For
              questions, please contact our support team.
            </p>
            <p>
              1. Do not change your account password until the transaction is
              complete or points are transferred. If you must change it, notify
              us immediately. Failure to do so may be seen as fraudulent and
              result in payment reversal.
            </p>
            <p>
              2. Your miles will be used to redeem airline tickets for
              third-party passengers. You may receive emails about the
              redemption; forward any questions to us.
            </p>
            <p>
              3. We need access to the account until the ticket is used (or the
              passenger has flown and returned).
            </p>
            <p>
              4. We only use the purchased miles or points, even if more
              accrue.If the account lacks enough miles to book a ticket, we may
              add additional miles, which belong to us.
            </p>
            <p>
              5. Selling miles and points is legal but against airline terms. Do
              not disclose the sale.
            </p>
            <p>
              Email us at{" "}
              <a
                href="mailto:noa@themilesmarket.com"
                className="text-accent font-semibold"
              >
                noa@themilesmarket.com
              </a>{" "}
              for questions.
            </p>
            <div className="pt-2">
              <Button
                variant="link"
                onClick={handleClose}
                className="font-semibold underline focus:outline-none focus:ring-2 focus:ring-accent focus:ring-opacity-50 rounded px-2 py-1 transition-all duration-200 hover:bg-accent hover:bg-opacity-10"
                type="button"
              >
                Read less
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value=""></AccordionItem>
      </Accordion>

      <div className="mt-2 flex items-center gap-4 px-1 xl:gap-1">
        <Checkbox
          id="terms"
          className="h-5 w-5"
          checked={agreedToTerms}
          onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
        />
        <Label htmlFor="terms" className="text-sm font-medium">
          I agree to The Miles Market's Terms and Conditions and authorize this
          transaction.
        </Label>
      </div>
    </div>
  );
}
